/**
 * Mantenimiento Pagos CXP — migración web de ares8540.
 *
 * Lista los pagos a suplidores por rango de fechas (y suplidor opcional) y
 * permite:
 *   - Anular un pago vigente: la anulación de la web revierte TODAS las
 *     facturas del pago (el monto aplicado se resta y las facturas se
 *     reabren) y bloquea la doble anulación.
 *   - Reimprimir el comprobante de pago con los datos de la fila.
 *
 * La impresión de cheques con plantillacheque del escritorio no se migró.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { encabezadoEmpresaHtml } from "lib/impresionhtml";

const filtrarSuplidores = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.descrip || ""} ${o.rnc || ""}`),
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin pagos en el rango
      </Typography>
    </Stack>
  );
}

// Comprobante de pago a suplidor (impresión con iframe oculto).
function imprimirComprobantePago(row, compania) {
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Comprobante de Pago a Suplidor</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:480px;margin:0 auto">
      ${encabezadoEmpresaHtml(compania)}
      <p style="text-align:center;margin:0 0 14px;font-weight:bold">Comprobante de Pago a Suplidor</p>
      <table style="width:100%;border-collapse:collapse;margin-bottom:14px">
        <tr><td style="padding:3px 8px;color:#555"># Pago</td><td style="padding:3px 8px;font-weight:bold">${
          row.id || ""
        }</td></tr>
        <tr><td style="padding:3px 8px;color:#555">Fecha</td><td style="padding:3px 8px;font-weight:bold">${
          row.fecha || ""
        }</td></tr>
        <tr><td style="padding:3px 8px;color:#555">Suplidor</td><td style="padding:3px 8px;font-weight:bold">${
          row.suplidor || ""
        }</td></tr>
        <tr><td style="padding:3px 8px;color:#555">Facturas</td><td style="padding:3px 8px;font-weight:bold">${
          row.facturas || ""
        }</td></tr>
      </table>
      <p style="text-align:right;font-weight:bold;margin:6px 0 14px">TOTAL: RD$ ${fmtMoney(
        row.valorpagado
      )}</p>
      <table style="width:100%;border-collapse:collapse;margin-top:10px">
        ${
          row.tipodepago
            ? `<tr><td style="padding:3px 8px;color:#555">Tipo de pago</td><td style="padding:3px 8px;font-weight:bold">${row.tipodepago}</td></tr>`
            : ""
        }
        ${
          row.banco
            ? `<tr><td style="padding:3px 8px;color:#555">Banco</td><td style="padding:3px 8px;font-weight:bold">${row.banco}</td></tr>`
            : ""
        }
        ${
          row.referencia
            ? `<tr><td style="padding:3px 8px;color:#555">Referencia</td><td style="padding:3px 8px;font-weight:bold">${row.referencia}</td></tr>`
            : ""
        }
        ${
          row.descripcion
            ? `<tr><td style="padding:3px 8px;color:#555">Descripción</td><td style="padding:3px 8px;font-weight:bold">${row.descripcion}</td></tr>`
            : ""
        }
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Preparado Por</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function MantenimientoPagosCxp({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [suplidores, setSuplidores] = useState([]);
  const [cargandoSuplidores, setCargandoSuplidores] = useState(true);
  const [suplidor, setSuplidor] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compania, setCompania] = useState(null);

  const cargar = async (sup = suplidor) => {
    setLoading(true);
    try {
      const payload = { fechadesde: fecha1, fechahasta: fecha2 };
      if (sup) payload.suplidor = sup.id;
      const { ok, data } = await post("/api/cxp_pagos_listar", payload);
      if (ok) {
        setRows(data.pagos || []);
      } else {
        showAlert({ message: data.Error || "No se pudieron listar los pagos", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cargarSuplidores = async () => {
      setCargandoSuplidores(true);
      try {
        const { ok, data } = await post("/api/cxp_suplidores", {});
        if (ok) setSuplidores(data.suplidores || []);
        else
          showAlert({
            message: data.Error || "No se pudieron cargar los suplidores",
            color: "error",
          });
      } finally {
        setCargandoSuplidores(false);
      }
    };
    cargarSuplidores();
    cargar();
    // Datos de la empresa para el encabezado del comprobante.
    post("/api/cxp_config", {}).then(({ ok, data }) => {
      if (ok) setCompania(data.compania || null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Anulación ----
  const [target, setTarget] = useState(null);
  const [anulando, setAnulando] = useState(false);

  const anular = async () => {
    setAnulando(true);
    try {
      const { ok, data } = await post("/api/cxp_pago_anular", { pago: target.id });
      if (ok) {
        showAlert({ message: data.ok || "Pago anulado", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo anular el pago", color: "error" });
      }
    } finally {
      setAnulando(false);
    }
  };

  const columns = [
    { field: "id", headerName: "Pago#", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "suplidor", headerName: "Suplidor", flex: 1, minWidth: 180 },
    { field: "tipodepago", headerName: "Tipo de pago", width: 130 },
    { field: "banco", headerName: "Banco", width: 130 },
    { field: "facturas", headerName: "Facturas", width: 140 },
    {
      field: "valorpagado",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.anulado ? (
          <Chip
            size="small"
            label="Anulado"
            sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Vigente"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setTarget(params.row)}
            disabled={!!params.row.anulado}
            sx={btnOutline(FP.color.danger)}
          >
            Anular
          </Button>
          <Button
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => imprimirComprobantePago(params.row, compania)}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Mantenimiento Pagos CXP"
          subtitle="Consulta, anula y reimprime los pagos a suplidores de cuentas x pagar"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarSuplidores}
                options={suplidores}
                loading={cargandoSuplidores}
                value={suplidor}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.descrip || "")}
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) => setSuplidor(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Suplidor (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={() => cargar()}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} pagos
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmación de anulación */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>Anular pago</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Pago <b>{target?.id}</b> a <b>{target?.suplidor}</b> por RD${" "}
            <b>{fmtMoney(target?.valorpagado)}</b>. La anulación revierte los montos aplicados y
            reabre las facturas del pago. ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              anulando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={anular}
            disabled={anulando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, anular
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
